import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Chip,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { ACTIVITY_TYPE_COLORS } from '../plannerMockData';
import { ACTIVITY_TYPE_ICONS } from './ActivityBlock';
import { getActivityLabel } from './WeeklyGrid';
import { WEEKDAY_LABELS, DAILY_TARGET_MINUTES, formatDuration, formatWeekRange, getDayKey, minutesToTimeLabel } from '../plannerUtils';

const DAY_MINUTES = 24 * 60;
const DRAG_MIME = 'application/x-planner-activity';

const activitiesOverlap = (a, b) => a.startMinutes < b.endMinutes && b.startMinutes < a.endMinutes;

const dayHasOverlap = (dayActivities) => {
  for (let i = 0; i < dayActivities.length; i += 1) {
    for (let j = i + 1; j < dayActivities.length; j += 1) {
      if (activitiesOverlap(dayActivities[i], dayActivities[j])) return true;
    }
  }
  return false;
};

const WeekDayColumn = ({
  date,
  label,
  activities,
  readOnly,
  onAddActivity,
  onEditActivity,
  onDeleteActivity,
  onCloneActivity,
  onSwapActivities,
  onMoveActivityToDay,
}) => {
  const dayKey = getDayKey(date);
  const [dragOver, setDragOver] = useState(false);
  const sorted = [...activities].sort((a, b) => a.startMinutes - b.startMinutes);
  const totalMinutes = sorted.reduce((sum, a) => sum + (a.endMinutes - a.startMinutes), 0);
  const belowMinimum = totalMinutes < DAILY_TARGET_MINUTES;
  const hasOverlap = dayHasOverlap(sorted);
  const occupiedUntil = sorted.reduce((max, a) => Math.max(max, a.endMinutes), 0);
  const suggestedStart = Math.min(occupiedUntil, DAY_MINUTES - 30);

  const readDragPayload = (e) => {
    try {
      return JSON.parse(e.dataTransfer.getData(DRAG_MIME));
    } catch {
      return null;
    }
  };

  const handleDrop = (e, targetActivity) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const dragged = readDragPayload(e);
    if (!dragged || readOnly) return;
    if (dragged.day === dayKey) {
      // Same day: drop onto another card swaps their times — e.g. "9:00 becomes 10:00 and 10:00 becomes 9:00".
      if (targetActivity && targetActivity.id !== dragged.id) {
        onSwapActivities(dragged.id, targetActivity.id);
      }
    } else {
      // Different day: keep the time, just move it here.
      onMoveActivityToDay(dragged.id, dayKey);
    }
  };

  return (
    <Box
      className={`planner-week-col${dragOver ? ' drag-over' : ''}`}
      onDragOver={(e) => {
        if (readOnly) return;
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => handleDrop(e, null)}
    >
      <Box className="planner-week-col-header">
        <Typography variant="subtitle2" fontWeight={700}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {date.getDate().toString().padStart(2, '0')} {date.toLocaleString('en-GB', { month: 'short' })}
        </Typography>
      </Box>

      <Box className="planner-week-col-stats">
        <Chip
          size="small"
          label={formatDuration(totalMinutes)}
          color={belowMinimum ? 'default' : 'success'}
          variant={belowMinimum ? 'outlined' : 'filled'}
        />
        {hasOverlap && (
          <Tooltip title="This day has overlapping activities">
            <WarningAmberIcon color="error" fontSize="small" />
          </Tooltip>
        )}
      </Box>

      <Box className="planner-week-col-bar">
        <Box
          className="planner-week-col-bar-fill"
          sx={{ width: `${Math.min((totalMinutes / DAY_MINUTES) * 100, 100)}%` }}
        />
        <Tooltip title="8h minimum">
          <Box
            className="planner-week-col-bar-marker"
            style={{ left: `${(DAILY_TARGET_MINUTES / DAY_MINUTES) * 100}%` }}
          />
        </Tooltip>
      </Box>

      <Box className="planner-week-col-list">
        {sorted.length === 0 && (
          <Typography variant="caption" color="text.secondary" className="planner-week-col-empty">
            No activities yet — drag one here, or add below.
          </Typography>
        )}
        {sorted.map((a) => {
          const color = ACTIVITY_TYPE_COLORS[a.type] || '#546e7a';
          const TypeIcon = ACTIVITY_TYPE_ICONS[a.type] || MoreHorizIcon;
          return (
            <Box
              key={a.id}
              className={`planner-week-item${readOnly ? ' readonly' : ''}`}
              style={{ borderLeftColor: color }}
              draggable={!readOnly}
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ id: a.id, day: a.day }));
              }}
              onDragOver={(e) => {
                if (readOnly) return;
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => handleDrop(e, a)}
              onClick={() => !readOnly && onEditActivity(a)}
            >
              <TypeIcon fontSize="inherit" style={{ color }} />
              <Box className="planner-week-item-text">
                <Typography variant="caption" fontWeight={600} noWrap title={getActivityLabel(a)}>
                  {getActivityLabel(a)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {minutesToTimeLabel(a.startMinutes)}–{minutesToTimeLabel(a.endMinutes)}
                </Typography>
              </Box>
              {!readOnly && (
                <>
                  <IconButton
                    size="small"
                    aria-label="Duplicate activity"
                    title="Duplicate"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCloneActivity(a);
                    }}
                  >
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Delete activity"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteActivity(a.id);
                    }}
                  >
                    <DeleteOutlineIcon fontSize="inherit" />
                  </IconButton>
                </>
              )}
            </Box>
          );
        })}
      </Box>

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => onAddActivity(dayKey, suggestedStart)}
        disabled={readOnly}
        sx={{ mt: 1 }}
      >
        Add
      </Button>
    </Box>
  );
};

const WeekPlannerDialog = ({
  open,
  onClose,
  weekStart,
  weekDates,
  activities,
  readOnly,
  onAddActivity,
  onEditActivity,
  onDeleteActivity,
  onCloneActivity,
  onSwapActivities,
  onMoveActivityToDay,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth sx={{ '& .MuiDialog-paper': { height: '85vh' } }}>
    <DialogTitle>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          Plan Full Week
          <Typography variant="body2" color="text.secondary">
            {formatWeekRange(weekStart)}
          </Typography>
        </Box>
        <IconButton edge="end" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </Box>
    </DialogTitle>
    <DialogContent dividers sx={{ p: 2 }}>
      {!readOnly && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Drag a card onto another to swap their times, or onto a different day to move it there.
        </Typography>
      )}
      <Box className="planner-week-grid">
        {weekDates.map((date, i) => (
          <WeekDayColumn
            key={getDayKey(date)}
            date={date}
            label={WEEKDAY_LABELS[i]}
            activities={activities.filter((a) => a.day === getDayKey(date))}
            readOnly={readOnly}
            onAddActivity={onAddActivity}
            onEditActivity={onEditActivity}
            onDeleteActivity={onDeleteActivity}
            onCloneActivity={onCloneActivity}
            onSwapActivities={onSwapActivities}
            onMoveActivityToDay={onMoveActivityToDay}
          />
        ))}
      </Box>
    </DialogContent>
    <DialogActions sx={{ px: 3, py: 2 }}>
      <Button onClick={onClose} variant="contained">
        Done
      </Button>
    </DialogActions>
  </Dialog>
);

export default WeekPlannerDialog;
